// Settings ▸ Objetivos: per-game unit-economics targets. There are no built-in thresholds; a metric without a
// target is shown without colour. Rates and ROAS are typed as percentages and stored as fractions.

import type { Game, Target, TargetMetric } from '@growth/client';
import { useState } from 'react';
import { Card, ErrorBox } from '../components/ui.tsx';
import { invalidateCache, useAction, useApi, useSession } from '../session.tsx';

const LABEL: Record<string, string> = {
  cpi: 'CPI',
  d1: 'Retención D1',
  d7: 'Retención D7',
  d30: 'Retención D30',
  ltv_d7: 'LTV D7',
  ltv_d30: 'LTV D30',
  roas_d7: 'ROAS D7',
  roas_d30: 'ROAS D30',
  cpi_ltv_d30: 'CPI / LTV D30',
  payback_days: 'Payback (días)',
  arpdau: 'ARPDAU',
  ctr: 'CTR',
  ipm: 'IPM',
  install_cvr: 'Conversión clic → instalación',
  store_cvr: 'Conversión de la ficha (Play)',
  tutorial: 'Tutorial completado',
};

/** Percent inputs for rates and ROAS (35 → 0.35); the rest as typed. */
const asPercent = (metric: string, m: TargetMetric) => m.unit === 'rate' || metric.startsWith('roas');

function unitLabel(metric: string, m: TargetMetric, ccy: string): string {
  if (asPercent(metric, m)) return '%';
  if (m.unit === 'money') return ccy;
  if (m.unit === 'days') return 'días';
  if (metric === 'ipm') return 'por 1000';
  return '×';
}

function TargetRow({ gameId, metric, def, target, ccy, onSaved }: { gameId: string; metric: string; def: TargetMetric; target: Target | undefined; ccy: string; onSaved: () => void }) {
  const { api, isAdmin } = useSession();
  const action = useAction();
  const pct = asPercent(metric, def);
  const show = (v: number | undefined) => (v === undefined ? '' : String(pct ? Math.round(v * 10000) / 100 : v));
  const [green, setGreen] = useState(show(target?.green));
  const [red, setRed] = useState(show(target?.red));
  const [note, setNote] = useState(target?.note ?? '');
  const parse = (v: string) => (pct ? Number(v) / 100 : Number(v));
  const save = async () => {
    if (await action.run(() => api.setTarget(gameId, metric, { green: parse(green), red: parse(red), note: note || null }))) onSaved();
  };
  const remove = async () => {
    if (await action.run(() => api.deleteTarget(gameId, metric))) {
      setGreen('');
      setRed('');
      setNote('');
      onSaved();
    }
  };
  const unit = unitLabel(metric, def, ccy);
  const input = (label: string, sign: string, value: string, set: (v: string) => void) => (
    <label className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
      <span className="muted small">
        {label} {sign}
      </span>
      <input className="input" style={{ width: 90 }} type="number" step="any" min={0} value={value} onChange={(e) => set(e.target.value)} disabled={!isAdmin} aria-label={`${metric}: umbral ${label}`} />
      <span className="muted small">{unit}</span>
    </label>
  );
  return (
    <tr>
      <td title={def.description}>
        <strong>{LABEL[metric] ?? def.label}</strong>
        <br />
        <span className="muted small">{def.direction === 'higher' ? 'más alto es mejor' : 'más bajo es mejor'}</span>
      </td>
      <td>{input('verde', def.direction === 'higher' ? '≥' : '≤', green, setGreen)}</td>
      <td>{input('rojo', def.direction === 'higher' ? '<' : '>', red, setRed)}</td>
      <td>
        <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} disabled={!isAdmin} placeholder="nota (opcional)" aria-label={`${metric}: nota`} />
      </td>
      <td>
        <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
          {isAdmin && (
            <button type="button" className="btn small primary" disabled={action.busy || green === '' || red === ''} onClick={save}>
              Guardar
            </button>
          )}
          {isAdmin && target && (
            <button type="button" className="btn small" disabled={action.busy} onClick={remove}>
              Quitar
            </button>
          )}
          {action.error && (
            <span className="small" style={{ color: 'var(--critical)' }}>
              {action.error.replace(/^HTTP 400 bad_request: /, '')}
            </span>
          )}
        </span>
      </td>
    </tr>
  );
}

export function TargetsTab({ game }: { game: Game }) {
  const targets = useApi<{ targets: Target[]; metrics: Record<string, TargetMetric> }>(`targets:${game.id}`, (api) => api.targets(game.id));
  const ccy = (game.settings.reporting_currency as string | undefined) ?? 'EUR';
  const onSaved = () => {
    invalidateCache('eco');
    invalidateCache('rev-summary');
    targets.reload();
  };
  const d = targets.data;
  return (
    <Card
      title="Objetivos de unit economics"
      subtitle="VERDE en el objetivo o mejor, ROJO peor que el umbral rojo, AMARILLO entre ambos. No hay umbrales por defecto: cada juego define los suyos (y sin objetivo la métrica se muestra sin color)."
    >
      {targets.error && <ErrorBox error={targets.error} onRetry={targets.reload} />}
      {d && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                {['Métrica', 'Verde', 'Rojo', 'Nota', ''].map((h) => (
                  <th key={h} scope="col">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(d.metrics).map(([metric, def]) => {
                const t = d.targets.find((x) => x.metric === metric);
                return <TargetRow key={`${metric}:${t?.updated_at ?? ''}`} gameId={game.id} metric={metric} def={def} target={t} ccy={ccy} onSaved={onSaved} />;
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="small muted" style={{ margin: '6px 0 0' }}>
        Retenciones y ROAS en porcentaje (ROAS 100 % = se recupera el gasto). Con menos de 30 jugadores la métrica sale como «muestra pequeña», sin color.
      </p>
    </Card>
  );
}
